import styled from "styled-components";
import EditIcon from "@assets/icons/eidt-button.svg";
import { useContext, useRef } from "react";
import { OnboardingContext } from "@context/onboardingContext";
import { useNavigate } from "react-router-dom";
import ROUTES from "@constants/routes";
import TextButton from "./TextButton";
import { clearRememberedPreferences } from "@utils/onboardingPreferenceUtil";

const Container = styled.div`
  display: flex;
  width: 335px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 12px;
  background: var(--bg-card);
  box-shadow: var(--shadow-lg);
  z-index: 100;
`;

const Wrapper = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid var(--border-default);
`;

const ProfileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
`;

const Profile = styled.div`
  color: var(--text-black);
  font-size: var(--font-size-2xl);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-3xl);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  color: var(--text-link);
  font-size: var(--font-size-2xl);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-3xl);
  text-decoration-line: underline;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.div`
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-base);
`;

const Value = styled.div`
  color: var(--text-black);
  font-size: var(--font-size-xl);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-xl);
`;

const EditProfile = ({
  onEdit,
  onFeedBackClick,
}: {
  onEdit: () => void;
  onFeedBackClick: () => void;
}) => {
  const { userData, clearOnboardingData } = useContext(OnboardingContext) || {};
  const { location, district, role, practiceArea } = userData || {};
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const handleEdit = () => {
    // Clear onboarding data
    clearOnboardingData?.();
    
    // NEW: Clear remembered preferences so suggestions show again
    clearRememberedPreferences();

    // Navigate back to guidelines start
    navigate(ROUTES.guidelines.to, {
      replace: true,
      state: {
        resetGuidelines: true,
      },
    });
    
    onEdit();
  };

  return (
    <Container ref={containerRef}>
      <Wrapper>
        <ProfileWrapper>
          <Profile>Profile</Profile>
          <ButtonContainer>
            <EditButton onClick={handleEdit}>Edit</EditButton>
            <EditIcon />
          </ButtonContainer>
        </ProfileWrapper>

        <InfoContainer>
          <div>
            <Label>{district! ? "District" : "Location"}</Label>
            <Value>{district ?? location}</Value>
          </div>
          <div>
            <Label>Role</Label>
            <Value>{role}</Value>
          </div>
          <div>
            <Label>Practice Area</Label>
            <Value>{practiceArea}</Value>
          </div>
        </InfoContainer>
        <TextButton onClick={onFeedBackClick}>Give feedback</TextButton>
      </Wrapper>
    </Container>
  );
};

export default EditProfile;